import React, { useState, useMemo, useEffect } from 'react';
import { 
  DollarSign, 
  ChevronDown, 
  ChevronUp, 
  Edit2, 
  Save, 
  X, 
  PoundSterlingIcon, 
  PlusCircle,
  Calculator,
  TrendingUp,
  AlertCircle,
  Send ,
  Bot,
  Info
} from 'lucide-react';

import RentalProfileForm from './RentalProfiling';
import { getRentalProfiling } from '../services/rentalProfilingService';
import approvalService from "../services/approvalService";
import { getIncentivesData } from '../services/mechanicsService';

const AI_RECEPTIONIST_MONTHLY = 10;        // included in calculations
const AI_RECEPTIONIST_INBOUND_PPM = 0.15;  // disclaimer only, NOT included

const DiscountApprovalButton = ({
  discountLoad,
  leaseCalculations,
  leaseCalculatorData,
  connectionId,
  hardwareItems,
  additionalHardware,
  currentLines,
  dataBroadband,
  lineRequirements,
  telecomServices,
  charges,
  adminUpdate
}) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [note, setNote] = useState("");
  const [noteData, setNoteData] = useState(null);
  const [status, setStatus] = useState(null);
  const [showStatusAlert, setShowStatusAlert] = useState(false);

  const isCommissionNegative = leaseCalculations.salesRepUnits < 0;
  const userRole = localStorage.getItem('Role');
  const isAdmin = userRole === 'Admin';

  const isAdminUpdated = adminUpdate === 1;

  const checkStatus = async () => {
    if (isAdminUpdated) return;
    
    try {
      const token = localStorage.getItem('authToken');

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/notes/${connectionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await res.json();

      if (data?.status && data.status !== status) {
        const isNewApproval = data.status === "approved" && status !== "approved";
        const isNewRejection = data.status === "rejected" && status !== "rejected";
        const isEdit = data.status === "edit" && status !== "edit";

        setStatus(data.status);
        setRequestSent(true);
        setNoteData(data);

        if (isNewApproval || isNewRejection || isEdit) {
          setShowStatusAlert(true);
        }
      }

      if (data?.note && !note) {
        setNote(data.note);
      }

      if (data?.status === "pending") {
        setTimeout(checkStatus, 5000);
      }
    } catch (err) {
      console.error("Error fetching status:", err);
    }
  };

  const StatusAlertPopup = () => {
    if (isAdminUpdated || !showStatusAlert) return null;
    const isApproved = status === "approved";
    const isRejected = status === "rejected";
    const isEditing = status === "edit";

    const title = isApproved
      ? "🎉 Approval Granted!"
      : isRejected
      ? "❌ Approval Rejected"
      : "🛠️ Admin Editing";

    const message = isApproved
      ? "Your discount/commission request has been approved by admin. You can proceed with the lease."
      : isRejected
      ? "Your discount/commission request has been rejected by admin."
      : "Admin is reviewing and editing this request. Please check existing contracts and await further instructions.";

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div
            className={`flex items-center mb-4 ${
              isApproved
                ? "text-green-600"
                : isRejected
                ? "text-red-600"
                : "text-blue-600"
            }`}
          >
            {isApproved ? (
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : isRejected ? (
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 4H9v5h2V6zm0 7H9v2h2v-2z" />
              </svg>
            )}
            <h3 className="text-lg font-bold">{title}</h3>
          </div>

          <p className="text-gray-700 mb-4">{message}</p>

          {noteData?.admin_note && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Admin Note:</h4>
              <p className="text-gray-600 text-sm">{noteData.admin_note}</p>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={() => {
                setShowStatusAlert(false);
                if (status === "edit") {
                  window.location.reload();
                }
              }}
              className={`px-4 py-2 rounded-md font-medium ${
                isApproved
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : isRejected
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              } transition-colors`}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (connectionId && !isAdminUpdated) {
      checkStatus();
    }
  }, [connectionId, isAdminUpdated]);

  const handleRequestApproval = async () => {
    if (isAdminUpdated) return;
    
    setIsRequesting(true);
    try {
      const notificationData = {
        type: isCommissionNegative
          ? "commission_approval_request"
          : "discount_approval_request",
        connection_id: connectionId?.toString() || null,
        discount_amount: Math.abs(discountLoad),
        requested_by: localStorage.getItem("userName") || "Unknown",
        requested_at: new Date().toISOString(),
        lease_details: {
          total_costs: leaseCalculations.totalCostsToUs,
          adjusted_rental: leaseCalculations.adjustedRental,
          actual_profit: leaseCalculations.actualProfit,
          profit_percentage: leaseCalculations.actualProfitPercentage,
          lease_payment: leaseCalculations.actualLeasePayment,
          sales_commission: leaseCalculations.salesRepUnits,
        },
        message: isCommissionNegative
          ? `Approval required: Sales commission is negative (£${leaseCalculations.salesRepUnits.toFixed(2)})`
          : `Contacting admin regarding discount (£${Math.abs(discountLoad).toFixed(2)})`,
        note: note.trim() || "",
      };

      const payload = {
        notification: notificationData,
        calculation_snapshot: {
          discountLoad,
          leaseCalculatorData,
          connectionId,
          hardwareItems,
          additionalHardware,
          currentLines,
          dataBroadband,
          lineRequirements,
          telecomServices,
          charges,
        },
      };

      await approvalService.sendDiscountApproval(payload);
      setRequestSent(true);
      setNote("");
      checkStatus();
    } catch (error) {
      alert("Failed to send message. Please contact admin directly.");
    } finally {
      setIsRequesting(false);
    }
  };

  const renderStatusBadge = () => {
    if (isAdminUpdated || !status) return null;

    let bgColor = "bg-gray-200 text-gray-700";
    let text = "Pending";

    if (status === "approved") {
      bgColor = "bg-green-100 text-green-800";
      text = "Approved ✅";
    } else if (status === "rejected") {
      bgColor = "bg-red-100 text-red-800";
      text = "Rejected ❌";
    } else if (status === "edit") {
      bgColor = "bg-blue-100 text-blue-800";
      text = "Editing ✏️";
    }

    return (
      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${bgColor}`}>
        {text}
      </span>
    );
  };

  if (isAdminUpdated && !isCommissionNegative) {
    return null;
  }

  return (
    <>
      {!isAdmin && <StatusAlertPopup />}

      <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            {isCommissionNegative && (
              <>
                <h6 className="text-sm font-semibold text-yellow-800 mb-1">
                  ⚠️ Commission Alert
                </h6>
                <p className="text-xs text-yellow-700 mb-3">
                  Sales commission is negative (£
                  {leaseCalculations.salesRepUnits.toFixed(2)}). You must request admin
                  approval before proceeding.
                </p>
              </>
            )}

            {isAdminUpdated ? (
              <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-700 font-medium">
                  ✅ This configuration has been updated by admin. No further approval needed.
                </p>
              </div>
            ) : (
              <>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add your note for the admin..."
                  className="border rounded p-2 w-full mt-2"
                />

                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={handleRequestApproval}
                    disabled={isRequesting || requestSent || isAdminUpdated}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      requestSent || isAdminUpdated
                        ? "bg-green-500 text-white cursor-not-allowed"
                        : isRequesting
                        ? "bg-yellow-400 text-yellow-800 cursor-wait"
                        : "bg-yellow-500 text-white hover:bg-yellow-600"
                    }`}
                  >
                    {isAdminUpdated
                      ? "✓ Admin Updated"
                      : requestSent
                      ? "✓ Sent"
                      : isRequesting
                      ? "Sending..."
                      : isCommissionNegative
                      ? "📤 Request Admin Approval"
                      : "📧 Contact Admin"}
                  </button>

                  {renderStatusBadge()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const CostComparisonComponent = ({ 
  selectedConnectionId, 
  charges, 
  totalMonthlyCost,
  itServicesTotal,
  isHostedLifetimeLicenses,
  leasedLines,
  totalEquipmentCost,
  mobileData,
  onLeaseDataUpdate,
  leaseCalculatorData,
  commission,
  hardwareItems,
  additionalHardware,
  currentLines,
  dataBroadband,
  lineRequirements,
  telecomServices,
  adminUpdate,
  totalNewDigitalCost,
  webHostingCost
}) => {

  const [isCurrentMobileCostsOpen, setIsCurrentMobileCostsOpen] = useState(false);
  const [serviceCharge, setServiceCharge] = useState(0);
  const [estimatedMobileCharges, setEstimatedMobileCharges] = useState(0);
  const userRole = localStorage.getItem('Role');
  const isAdmin = userRole === 'Admin';

  const [paymentProfile, setPaymentProfile] = useState(84);
  const [networkPayment, setnetworkPayment] = useState(12);
  const [discountLoad, setDiscountLoad] = useState(0);
  const [targetProfitMargin, setTargetProfitMargin] = useState(30);

  const [showRentalModal, setShowRentalModal] = useState(false);
  const [hasRentalData, setHasRentalData] = useState(false);
  const [rentalData, setRentalData] = useState(null);
  const [isEditingRental, setIsEditingRental] = useState(false);
  const [manuallyEditedFields, setManuallyEditedFields] = useState({
    networkSettlement: false,
    leasedLine: false
  });

  const [incentivesEnabled, setIncentivesEnabled] = useState(false);
  const [cost, setCost] = useState('');
  const [aiReceptionistEnabled, setAiReceptionistEnabled] = useState(false);

  const DATA_PLAN_PRICES = {
    unlimited: 25,
    '40 gb': 21,
    '20 gb': 18,
    '10 gb': 15
  };

  const MOBILE_PHONE_PRICES = {
    'director_premium': 1400,
    'director': 1000,
    'manager': 750,
    'standard': 400
  };

  const LEASE_RATES = {
    84: 17.2,
    72: 19,
    60: 23,
    48: 27,
    36: 36
  };

  const NETWORK_CREDIT_OPTIONS = [12, 6, 18];
  
  const [editing, setEditing] = useState({
    callCharges: false,
    currentCallCharges: false,
    leasedLine: false,
    itServiceCharge: false 
  });

  const initialNetworkSettlement = 0;
  
  const [tempValues, setTempValues] = useState({
    networkSettlement: initialNetworkSettlement,
    currentCallCharges: 0,
    callCharges: charges?.call_charges || 0,
    leasedLine: 0,
    itServiceCharge: 0
  });

  const [isHydrated, setIsHydrated] = useState(false);


  useEffect(() => {
    if (leaseCalculatorData && !isHydrated) {
      setPaymentProfile(leaseCalculatorData.paymentProfile ?? 84);
      setnetworkPayment(parseFloat(leaseCalculatorData.networkPayment ?? 12));
      setDiscountLoad(parseFloat(leaseCalculatorData.discountLoad) || 0);

      const savedNetworkSettlement = leaseCalculatorData.networkSettlementContribution;
      const defaultNetworkSettlement = isHostedLifetimeLicenses 
        ? -Math.min(isHostedLifetimeLicenses * 100, 300) 
        : 0;

      const hasSavedValue = savedNetworkSettlement !== null && 
                            savedNetworkSettlement !== undefined &&
                            savedNetworkSettlement !== '';
      
      const networkSettlementValue = hasSavedValue 
        ? Number(savedNetworkSettlement) 
        : defaultNetworkSettlement;

     
        const savedLeasedLine = leaseCalculatorData.leased_line; // ← snake_case
        const hasSavedLeasedLine = savedLeasedLine != null; // ← covers 0 as valid
        const leasedLineValue = hasSavedLeasedLine 
          ? Number(savedLeasedLine) 
          : (Number(leasedLines) || 0); 
        setTempValues({
          networkSettlement: networkSettlementValue,
          currentCallCharges: Number(leaseCalculatorData.currentCallCharges) ?? 0,
          callCharges: Number(leaseCalculatorData.estimatedCallCharges) ?? 0,
          leasedLine: leasedLineValue, // ← now actually 20, not 0
          itServiceCharge: Number(leaseCalculatorData.it_service_charge) || 0, 
        });

        const savedAiReceptionist = leaseCalculatorData.ai_receptionist_enabled;
        if (savedAiReceptionist !== null && savedAiReceptionist !== undefined && savedAiReceptionist !== '') {
          setAiReceptionistEnabled(
            savedAiReceptionist === true || savedAiReceptionist === 'true' || Number(savedAiReceptionist) === 1
          );
        }

      if (hasSavedValue) {
        setManuallyEditedFields(prev => ({
          ...prev,
          networkSettlement: true
        }));
      }

      if (hasSavedLeasedLine) {
        setManuallyEditedFields(prev => ({
          ...prev,
          leasedLine: true
        }));
      }

      setIsHydrated(true);
    }
  }, [leaseCalculatorData, isHydrated, isHostedLifetimeLicenses]);

 useEffect(() => {
  if (!isHydrated || manuallyEditedFields.leasedLine) return;

  const savedLeasedLine = leaseCalculatorData?.leased_line;
  const hasSavedValue = savedLeasedLine != null; // ← covers 0 as valid
  if (hasSavedValue) return;

  setTempValues(prev => {
    const newVal = Number(leasedLines) || 0;
    if (prev.leasedLine !== newVal) {
      return { ...prev, leasedLine: newVal };
    }
    return prev;
  });
}, [leasedLines, manuallyEditedFields.leasedLine, isHydrated, leaseCalculatorData?.leased_line]);
  const connectionId = selectedConnectionId?.selectedConnectionId || selectedConnectionId;
  
 
  useEffect(() => {
    const getRentalDetails = async () => {
      try {
        const jsonData = await getRentalProfiling(connectionId);
        
        if (jsonData && jsonData.status === 'success' && jsonData.data) {
          setRentalData(jsonData.data);
          setHasRentalData(true);
        } else {
          setHasRentalData(false);
          setRentalData(null);
        }
      } catch (error) {
        console.error("Failed to fetch rental data:", error);
        setHasRentalData(false);
        setRentalData(null);
      }
    };

    if (connectionId) {
      getRentalDetails();
    }
  }, [connectionId]);

  useEffect(() => {
    if (!charges) return;
    const total = (charges.service_charge || 0) + (charges.broadband || 0);
    setServiceCharge(total);

    setTempValues(prev => ({
      ...prev,
      currentCallCharges:
        prev.currentCallCharges !== undefined && prev.currentCallCharges !== null
          ? prev.currentCallCharges
          : Number(leaseCalculatorData?.currentCallCharges) || 0,
      callCharges: charges.call_charges || prev.callCharges || 0
    }));
  }, [charges, leaseCalculatorData]);


  useEffect(() => {
    if (!mobileData || !mobileData.data_plans) return;
     
    let total = 0;
    mobileData.data_plans.forEach(plan => {
      const planType = plan.data_allowance?.toLowerCase();
      if (planType && DATA_PLAN_PRICES[planType] && plan.count) {
        total += DATA_PLAN_PRICES[planType] * plan.count;
      }
    });
    
    setEstimatedMobileCharges(total);
  }, [mobileData]);

  

  useEffect(() => {
    if (!isHydrated || manuallyEditedFields.networkSettlement) {
      return;
    }

    const savedNetworkSettlement = leaseCalculatorData?.networkSettlementContribution;
    const hasSavedValue = savedNetworkSettlement !== null && 
                          savedNetworkSettlement !== undefined &&
                          savedNetworkSettlement !== '';
    
    if (hasSavedValue) {
      return;
    }

    const calculateNetworkSettlement = () => {
      if (!isHostedLifetimeLicenses) return 0;
      return -Math.min(isHostedLifetimeLicenses * 100, 300);
    };

    const newSettlement = calculateNetworkSettlement();
    
    setTempValues(prev => {
      if (prev.networkSettlement !== newSettlement) {
        return { ...prev, networkSettlement: newSettlement };
      }
      return prev;
    });
  }, [isHostedLifetimeLicenses, manuallyEditedFields.networkSettlement, isHydrated, leaseCalculatorData?.networkSettlementContribution]);

  const fetchIncentivesData = async () => {
    try {
      const response = await getIncentivesData();
      const data = response.data || response;
      if (data) {
        const { incentives_enabled, cost } = data;
        const enabled = Boolean(Number(incentives_enabled));
        setIncentivesEnabled(enabled);
        setCost(cost);
      }
    } catch (error) {
      console.error("❌ Error fetching incentives data:", error);
    }
  };

  useEffect(() => {
    fetchIncentivesData();
  }, []);


  useEffect(() => {
    setManuallyEditedFields({
      networkSettlement: false,
      leasedLine: false
    });
  }, [connectionId]);

  const currentMobileCost = useMemo(() => {
    if (!mobileData || !mobileData.staff_levels) {
      return 0;
    }
    
    let total = 0;
    
    mobileData.staff_levels.forEach((phone, index) => {
      const phoneType = phone.staff_level?.toLowerCase();
      const count = parseInt(phone.count) || 0;
      const rate = MOBILE_PHONE_PRICES[phoneType] || 0;
      if (count > 0 && rate > 0) {
        total += rate * count;
      }
    });
      
    return total;
  }, [mobileData]);

  const rentalCostsTotal = useMemo(() => {
    if (!rentalData) return 0;
    
    const cashbackVisible = parseFloat(rentalData.cashback_visible) || 0;
    const linesTermination = parseFloat(rentalData.lines_termination) || 0;
    const callsTermination = parseFloat(rentalData.calls_termination) || 0;
    const currentLeaseBuyOuts = parseFloat(rentalData.current_lease_buy_outs) || 0;
    const mobileBuyouts = parseFloat(rentalData.mobile_buyouts) || 0;
    const otherCosts = parseFloat(rentalData.other_costs) || 0;
    
    return cashbackVisible + linesTermination + callsTermination + currentLeaseBuyOuts + mobileBuyouts + otherCosts;
  }, [rentalData]);

  const leaseCalculations = useMemo(() => {
    const networkSettlementNum = Math.abs(Number(tempValues.networkSettlement) || 0);
    const networkPaymentNum = Number(networkPayment) || 0;

    const networkSettlementTotal = networkSettlementNum * networkPaymentNum;
    
    let totalCostsToUs =
      totalEquipmentCost + rentalCostsTotal + currentMobileCost + networkSettlementTotal;

    if (incentivesEnabled && cost) {
      const incentiveCost = Number(cost) || 0;
      totalCostsToUs += incentiveCost;
    }
  
    const leaseRate = LEASE_RATES[paymentProfile] || 17.2;
    
    const desiredProfit = totalCostsToUs * (targetProfitMargin / 100);
    const requiredLeasePayment = totalCostsToUs + desiredProfit;
    const baseMonthlyRental = (requiredLeasePayment / 1000) * leaseRate;
    const adjustedRental = baseMonthlyRental + (discountLoad);
    const actualLeasePayment = (adjustedRental / leaseRate) * 1000;
    const actualProfit = actualLeasePayment - totalCostsToUs;
    const actualProfitPercentage = totalCostsToUs > 0 ? (actualProfit / totalCostsToUs) * 100 : 0;
    
    const commissionRate = commission ? commission / 100 : 0.17;
    const salesRepCommission = actualProfit * commissionRate;
    const salesRepUnits = salesRepCommission / 1000;

    return {
      totalCostsToUs,
      leaseRate,
      desiredProfit,
      requiredLeasePayment,
      baseMonthlyRental,
      adjustedRental,
      actualLeasePayment,
      actualProfit,
      actualProfitPercentage,
      salesRepCommission,
      salesRepUnits,
      commission,
      incentivesEnabled,
      cost
    };
  }, [totalEquipmentCost, rentalCostsTotal, currentMobileCost, paymentProfile, networkPayment, discountLoad, targetProfitMargin, tempValues.networkSettlement, commission, incentivesEnabled, cost]);
  const aiReceptionistCharge = aiReceptionistEnabled ? AI_RECEPTIONIST_MONTHLY : 0;
  const handleConfigureRentalProfile = () => {
    setIsEditingRental(false);
    setShowRentalModal(true);
  };

  const handleEditRentalProfile = () => {
    setIsEditingRental(true);
    setShowRentalModal(true);
  };

  const handleCloseRentalModal = () => {
    setShowRentalModal(false);
    setIsEditingRental(false);
  };

  const handleRentalSaveSuccess = async (updatedData) => {
    try {
      const refreshedData = await getRentalProfiling(connectionId);
      if (refreshedData && refreshedData.status === 'success' && refreshedData.data) {
        setRentalData(refreshedData.data);
        setHasRentalData(true);
      }
    } catch (error) {
      const dbStructuredData = {
        id: updatedData.id,
        connection_id: updatedData.connection_id,
        payment_profile: updatedData.paymentProfile,
        networkPayment: updatedData.networkPayment,
        rental_discount: updatedData.rentalDiscount,
        cash_discount: updatedData.cashDiscount,
        network_credit_length: updatedData.networkCreditLength,
        units_lease: updatedData.unitsLease,
        cashback_visible: updatedData.cashbackVisible,
        cashback_visible_reason: updatedData.cashbackVisibleReason,
        lines_termination: updatedData.linesTermination,
        lines_termination_reason: updatedData.linesTerminationReason,
        calls_termination: updatedData.callsTermination,
        calls_termination_reason: updatedData.callsTerminationReason,
        current_lease_buy_outs: updatedData.currentLeaseBuyOuts,
        current_lease_buy_outs_reason: updatedData.currentLeaseBuyOutsReason,
        mobile_buyouts: updatedData.mobileBuyouts,
        mobile_buyouts_reason: updatedData.mobileBuyoutsReason,
        other_costs: updatedData.otherCosts,
        other_costs_reason: updatedData.otherCostsReason,
        created_at: rentalData?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setRentalData(dbStructuredData);
      setHasRentalData(true);
    }
    
    setShowRentalModal(false);
    setIsEditingRental(false);
  };

  const handleEditToggle = (field) => {
    setEditing(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const fieldMap = {
    currentCallCharges: "currentCallCharges",
    callCharges: "estimatedCallCharges",
    networkSettlement: "networkSettlementContribution",
    leasedLine: "leasedLine",
    itServiceCharge: "it_service_charge"
  };

  const handleSave = (field) => {
    const newValue = Number(tempValues[field]) || 0;

    setEditing((prev) => ({ ...prev, [field]: false }));
    
    setManuallyEditedFields(prev => ({
      ...prev,
      [field]: true
    }));

    setTempValues((prev) => ({
      ...prev,
      [field]: newValue
    }));

    if (fieldMap[field]) {
      onLeaseDataUpdate?.((prev) => ({
        ...prev,
        [fieldMap[field]]: newValue
      }));
    }
  };


const handleCancel = (field) => {
  setEditing(prev => ({ ...prev, [field]: false }));
  const savedValue = field === 'leasedLine'
    ? leaseCalculatorData?.leased_line ?? 0   // ← snake_case for this field
    : leaseCalculatorData?.[fieldMap[field]] ?? 0;
  setTempValues(prev => ({ ...prev, [field]: savedValue }));
  setManuallyEditedFields(prev => ({ ...prev, [field]: false }));
};
  const handleInputChange = (field, value) => {
    setTempValues(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const currentChargesItems = useMemo(() => [
    { 
      label: 'Current Phone System Lease Rental', 
      value: charges?.equipment_rentals || 0,
      editable: false
    },
    { 
      label: 'Current Mobile Phone Charges', 
      value: mobileData?.existing_costs || 0,
      editable: false
    },
    { 
      label: 'Current Call Charges', 
      value: leaseCalculatorData.currentCallCharges || 0,
      editable: true,
      field: 'currentCallCharges',
    },
    { 
      label: 'Current Service Charges, including Line rental, diverts etc', 
      value: serviceCharge || 0,
      editable: false
    },
    { 
      label: 'Current Maintenance', 
      value: charges?.maintenance || 0,
      editable: false
    },
    {
      label: 'Other Charges',
      value: (charges?.media ?? 0) + (charges?.utilities ?? 0),
      editable: false
    }
  ], [charges, mobileData, serviceCharge, tempValues.currentCallCharges]);


  const newMonthlyChargesItems = useMemo(() => [
    { 
      label: 'Telephone System Lease Rental', 
      value: leaseCalculations.adjustedRental || 0,
      editable: false,
      isCalculated: true
    },
    { 
      label: 'Estimated Mobile Charges', 
      value: estimatedMobileCharges,
      editable: false
    },
    { 
      label: 'Estimated Call Charges', 
      value: tempValues.callCharges || 0,
      editable: true,
      field: 'callCharges',
    },
    { 
      label: isHostedLifetimeLicenses > 0 
        ? 'New service charges, inclusive local, national and mobile calls for full term' 
        : 'New Service charges', 
      value: (totalMonthlyCost || 0),
      editable: false
    },
    { 
      label: 'Network Settlement Contribution - Paid in lump sum (VAT EXEMPT) figure showing per month pro rata', 
      value: tempValues.networkSettlement,
      editable: true, 
      field: 'networkSettlement',
    },
    { 
      label: 'Leased Line', 
      value: tempValues.leasedLine,
      editable: true,
      field: 'leasedLine',
    },
    { 
      label: 'Digital Service Charges', 
      value: totalNewDigitalCost || 0,
      editable: false
    },
    { 
      label: 'IT Service Charge', 
      value: tempValues.itServiceCharge || 0,
      editable: true,
      field: 'itServiceCharge',
    },
    { 
      label: 'Microsoft Licences / IT Support',
      value: itServicesTotal  || 0,
      editable: false,
      field: 'itServiceTotal',
    },
    {
      label: 'AI Receptionist',
      value: aiReceptionistCharge,
      editable: false,
      isAiReceptionist: true,
    },
  ], [charges, estimatedMobileCharges, tempValues.callCharges, tempValues.currentCallCharges, tempValues.networkSettlement, tempValues.leasedLine, tempValues.itServiceCharge, totalMonthlyCost, isHostedLifetimeLicenses, leaseCalculations.adjustedRental, initialNetworkSettlement, totalNewDigitalCost,itServicesTotal , mobileData,aiReceptionistCharge]);

  const currentTotalPackageCost = useMemo(() => {
    return (
      (parseFloat(charges?.equipment_rentals) || 0) +
      (parseFloat(mobileData?.existing_costs) || 0) +
      (parseFloat(tempValues.currentCallCharges) || 0) +
      (parseFloat(serviceCharge) || 0) +
      (parseFloat(charges?.maintenance) || 0) +
      (parseFloat(charges?.media) || 0) +
      (parseFloat(charges?.utilities) || 0) 
    );
  }, [charges, mobileData, serviceCharge, tempValues.currentCallCharges]);

  const newTotalPackageCost = useMemo(() => {
    const sum = newMonthlyChargesItems.reduce((sum, item) => {
      const value = parseFloat(item.value) || 0;
      return sum + value;
    }, 0);
    return Number(sum) || 0;
  }, [newMonthlyChargesItems]);

  const leaseData = useMemo(() => ({
    paymentProfile,
    networkPayment,
    discountLoad,
    currentCallCharges: tempValues.currentCallCharges,
    estimatedCallCharges: tempValues.callCharges,
    networkSettlementContribution: tempValues.networkSettlement,
    leasedLine: tempValues.leasedLine,
    it_service_charge: tempValues.itServiceCharge || 0,
    adjustedRental: leaseCalculations.adjustedRental,
    actualLeasePayment: leaseCalculations.actualLeasePayment,
    actualProfit: leaseCalculations.actualProfit,
    salesRepCommission: leaseCalculations.salesRepCommission,
    current_lease_rental: charges?.equipment_rentals,
    current_mobile_charges: parseFloat(mobileData?.existing_costs),
    current_service_charges: serviceCharge,
    current_maintenance: charges?.maintenance,
    other_charges: charges?.media,
    current_total_cost: currentTotalPackageCost,
    estimated_mobile_charges: estimatedMobileCharges,
    new_service_charges: totalMonthlyCost,
    leased_line: tempValues.leasedLine,
    digital_service_charges: totalNewDigitalCost || 0,
    it_services_total: itServicesTotal || 0,
    ai_receptionist_enabled: aiReceptionistEnabled ? 1 : 0,
    ai_receptionist_charge: aiReceptionistCharge,
    ai_receptionist_inbound_ppm: AI_RECEPTIONIST_INBOUND_PPM, // informational only
    
  }), [
    paymentProfile,
    networkPayment,
    discountLoad,
    tempValues.currentCallCharges,
    tempValues.callCharges,
    tempValues.networkSettlement,
    tempValues.leasedLine,
    tempValues.itServiceCharge,
    leaseCalculations.adjustedRental,
    leaseCalculations.actualLeasePayment,
    leaseCalculations.actualProfit,
    leaseCalculations.salesRepCommission,
    charges?.equipment_rentals,
    mobileData?.existing_costs,
    serviceCharge,
    charges?.maintenance,
    charges?.media,
    currentTotalPackageCost,
    estimatedMobileCharges,
    totalMonthlyCost,
    totalNewDigitalCost,
    itServicesTotal,
    aiReceptionistEnabled,
    aiReceptionistCharge
  ]);

  const prevLeaseDataRef = React.useRef(null);

  useEffect(() => {
    const newData = JSON.stringify(leaseData);
    if (newData !== prevLeaseDataRef.current) {
      prevLeaseDataRef.current = newData;
      onLeaseDataUpdate?.(leaseData);
    }
  }, [leaseData, onLeaseDataUpdate]);

  const renderAiReceptionistRow = (item, index) => (
  <div key={index} className={`px-3 py-2 bg-purple-50 ${index < newMonthlyChargesItems.length - 1 ? 'border-b border-gray-200' : ''}`}>
    <div className="flex justify-between items-center">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={aiReceptionistEnabled}
          onChange={(e) => setAiReceptionistEnabled(e.target.checked)}
          className="h-4 w-4 text-purple-600 border-gray-300 rounded"
        />
        <Bot size={16} className="text-purple-600" />
        <span>{item.label} <span className="text-gray-500">(£{AI_RECEPTIONIST_MONTHLY.toFixed(2)} per month)</span></span>
      </label>
      <input
        type="text"
        value={`£${parseFloat(item.value || 0).toFixed(2)}`}
        readOnly
        className={`text-right w-20 border-none outline-none bg-transparent ${aiReceptionistEnabled ? '' : 'text-gray-400'}`}
      />
    </div>
    {aiReceptionistEnabled && (
      <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          <strong>Disclaimer:</strong> Inbound calls handled by the AI Receptionist are charged at{' '}
          <strong>15p per minute</strong>.
        </span>
      </div>
    )}
  </div>
);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-4 mt-4">
      
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-blue-200">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div 
            className="bg-gradient-to-r from-blue-800 via-blue-900 to-black text-white p-6 cursor-pointer flex-1 flex justify-between items-center hover:from-gray-700 hover:via-gray-800 hover:to-gray-900 transition-all duration-300"
            onClick={() => setIsCurrentMobileCostsOpen(!isCurrentMobileCostsOpen)}
          >
            <div className="flex items-center space-x-3">
              <PoundSterlingIcon className="w-6 h-6" />
              <h2 className="text-xl font-bold tracking-wide">COST COMPARISON & LEASE CALCULATOR</h2>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-300 text-sm font-medium">
                {isCurrentMobileCostsOpen ? 'Collapse' : 'Expand'}
              </span>
              <div className="bg-gray-700 rounded-full p-1">
                {isCurrentMobileCostsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
          </div>
        </div>
       
        {/* Main Content */}
        {isCurrentMobileCostsOpen && (
          <div className="max-w-7xl mx-auto p-6 bg-white border border-gray-300 rounded-md">
            <div className="grid grid-cols-1 gap-6 items-start text-sm">
              {!hasRentalData ? (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md text-center">
                  <p className="mb-4">Please configure Rental Profile first</p>
                  <button
                    onClick={handleConfigureRentalProfile}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Configure Rental Profile
                  </button>
                </div>
              ) : (
                <div className="mb-6 flex justify-end space-x-4">
                  <button
                    onClick={handleEditRentalProfile}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Edit2 size={16} />
                    Edit Rental Profile
                  </button>
                </div>
              )}

              {/* Session Summary */}
              <div className="grid grid-cols-1 gap-6 items-start text-sm mt-8">
                <div>
                  <h2 className="text-lg font-bold mb-4 text-center text-gray-800">SESSION SUMMARY</h2>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      
                      {isAdmin && ( 
                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-600 mb-2">Total Equipment Cost</h3>
                        <div className="text-2xl font-bold text-blue-600">
                          £{totalEquipmentCost.toFixed(2)}
                        </div>
                      </div>
                      )}

                      {isAdmin && ( 
                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-600 mb-2">Current Mobile Cost</h3>
                        <div className="text-2xl font-bold text-green-600">
                          £{currentMobileCost.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Premium Pricing Per Phone
                        </div>
                      </div>
                      )}

                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-600 mb-2">Total Additional Costs</h3>
                        <div className="text-2xl font-bold text-orange-600">
                          £{rentalCostsTotal.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Cashback + Termination + Buyouts
                        </div>
                      </div>

                    </div>

                    {isAdmin && (
                      <>
                      {mobileData && mobileData.staff_levels && mobileData.staff_levels.length > 0 && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Mobile Phone Cost Breakdown:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            {mobileData.staff_levels.map((phone, index) => {
                              const phoneType = phone.staff_level?.toLowerCase();
                              const count = parseInt(phone.count) || 0;
                              const rate = MOBILE_PHONE_PRICES[phoneType] || 0;
                              const subtotal = rate * count;
                              
                              return (
                                <div key={index} className="flex justify-between">
                                  <span className="text-gray-600">
                                    {phone.staff_level} ({count} phones @ £{rate.toFixed(2)}/phone):
                                  </span>
                                  <span className="font-medium">£{subtotal.toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Additional Costs Breakdown:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Cashback visible to customer:</span>
                            <span className="font-medium">£{(parseFloat(rentalData?.cashback_visible) || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Lines Termination Charges:</span>
                            <span className="font-medium">£{(parseFloat(rentalData?.lines_termination) || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Calls Termination Charges:</span>
                            <span className="font-medium">£{(parseFloat(rentalData?.calls_termination) || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Current Lease/Rental Buy Outs:</span>
                            <span className="font-medium">£{(parseFloat(rentalData?.current_lease_buy_outs) || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Mobile Buyouts:</span>
                            <span className="font-medium">£{(parseFloat(rentalData?.mobile_buyouts) || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Other Costs:</span>
                            <span className="font-medium">£{(parseFloat(rentalData?.other_costs) || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      </>
                    )}

                    {isAdmin && ( 
                    <div className="mt-6 pt-4 border-t-2 border-gray-300">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-gray-800">Total Costs to Us:</span>
                        <span className="text-2xl font-bold text-indigo-600">
                          £{(totalEquipmentCost + currentMobileCost + rentalCostsTotal).toFixed(2)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 text-right">
                        Equipment + Mobile + Additional Costs (Used in Lease Calculation)
                      </div>
                    </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Lease Calculator Section */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-4">
                  <Calculator className="w-6 h-6 text-green-600 mr-2" />
                  <h3 className="text-lg font-bold text-gray-800">Lease Calculator</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Profile (Months)
                    </label>
                    <select
                      value={paymentProfile}
                      onChange={(e) => setPaymentProfile(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(LEASE_RATES).map(([months]) => (
                        <option key={months} value={months}>
                          {months} months
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Network Settlement (Months)
                    </label>
                    <select
                      value={networkPayment}
                      onChange={(e) => setnetworkPayment(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {NETWORK_CREDIT_OPTIONS.map((months) => (
                        <option key={months} value={months}>
                          {months} months
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount/Load
                    </label>
                    <input
                      type="number"
                      value={discountLoad === 0 ? '' : discountLoad}
                      onChange={(e) => setDiscountLoad(parseFloat(e.target.value) || 0)}
                      step="0.1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      Positive = Loading, Negative = Discount
                    </div>
                  </div>
                </div>

                {/* Lease Calculation Results */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {isAdmin && ( 
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-600 mb-2">Required Monthly Rental</h4>
                    <div className="text-xl font-bold text-blue-600">
                      £{leaseCalculations.adjustedRental.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      What to quote customer
                    </div>
                  </div>
                  )}
                  {isAdmin && ( 
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-600 mb-2">Lease Payment to Us</h4>
                    <div className="text-xl font-bold text-green-600">
                      £{leaseCalculations.actualLeasePayment.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      What lease company pays upfront
                    </div>
                  </div>
                  )}
                  {isAdmin && ( 
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-600 mb-2">Actual Profit</h4>
                    <div className="text-xl font-bold text-purple-600">
                      £{leaseCalculations.actualProfit.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {leaseCalculations.actualProfitPercentage.toFixed(1)}% margin
                    </div>
                  </div>
                  )}
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-gray-600 mb-2">Sales Rep Commission</h4>
                    <div className="text-xl font-bold text-orange-600">
                      {leaseCalculations.salesRepUnits.toFixed(2)} units
                    </div>
                  </div>
                </div>

                {/* Calculation Breakdown */}
                {isAdmin && ( 
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h5 className="text-sm font-semibold text-gray-700 mb-3">Reverse Calculation Breakdown:</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Costs to Us:</span>
                      <span className="text-gray-600 text-xs">(Including Network Settlement Cost)</span>
                      <span className="font-medium">£{leaseCalculations.totalCostsToUs.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Desired Profit ({targetProfitMargin}%):</span>
                      <span className="font-medium">£{leaseCalculations.desiredProfit.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Required Lease Payment:</span>
                      <span className="font-medium">£{leaseCalculations.requiredLeasePayment.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base Monthly Rental:</span>
                      <span className="font-medium">£{leaseCalculations.baseMonthlyRental.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lease Rate ({paymentProfile} months):</span>
                      <span className="font-medium">{leaseCalculations.leaseRate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discount/Load:</span>
                      <span className="font-medium">
                        {discountLoad >= 0 ? '+' : ''}£{discountLoad.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <h6 className="text-xs font-semibold text-gray-700 mb-2">Reverse Calculation Formula Steps:</h6>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div><strong>1.</strong> Desired Profit = £{leaseCalculations.totalCostsToUs.toFixed(2)} × {targetProfitMargin}% = £{leaseCalculations.desiredProfit.toFixed(2)}</div>
                      <div><strong>2.</strong> Required Lease Payment = £{leaseCalculations.totalCostsToUs.toFixed(2)} + £{leaseCalculations.desiredProfit.toFixed(2)} = £{leaseCalculations.requiredLeasePayment.toFixed(2)}</div>
                      <div><strong>3.</strong> Base Monthly Rental = (£{leaseCalculations.requiredLeasePayment.toFixed(2)} ÷ 1000) × {leaseCalculations.leaseRate} = £{leaseCalculations.baseMonthlyRental.toFixed(2)}</div>
                      <div><strong>4.</strong> Final Rental = £{leaseCalculations.baseMonthlyRental.toFixed(2)} + {discountLoad} = £{leaseCalculations.adjustedRental.toFixed(2)}</div>
                      <div><strong>5.</strong> Verification: (£{leaseCalculations.adjustedRental.toFixed(2)} ÷ {leaseCalculations.leaseRate}) × 1000 = £{leaseCalculations.actualLeasePayment.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
                )}

                <DiscountApprovalButton 
                  discountLoad={discountLoad}
                  leaseCalculations={leaseCalculations}
                  leaseCalculatorData={leaseCalculatorData}
                  connectionId={connectionId}
                  hardwareItems={hardwareItems}
                  additionalHardware={additionalHardware}
                  currentLines={currentLines}
                  dataBroadband={dataBroadband}
                  lineRequirements={lineRequirements}
                  telecomServices={telecomServices}
                  charges={charges}
                  adminUpdate={adminUpdate}
                />
              </div>

              {/* Current Provider Mobiles */}
              <div>
                <h2 className="text-lg font-bold mb-2">Current Provider Mobiles (Excl VAT)</h2>
                
                <div className="border border-gray-300">
                  {currentChargesItems.map((item, index) => (
                    <div 
                      key={index} 
                      className={`flex justify-between px-3 py-2 ${index < currentChargesItems.length - 1 ? 'border-b border-gray-200' : ''} ${item.editable ? 'bg-blue-50' : ''}`}
                    >
                      <span>{item.label}</span>
                      
                      {item.editable ? (
                        <div className="flex items-center space-x-2">
                          {editing[item.field] ? (
                            <>
                              <input
                                type="number"
                                min="0"
                                value={tempValues[item.field]}
                                onChange={(e) => handleInputChange(item.field, e.target.value)}
                                className="w-24 px-2 py-1 border border-blue-300 rounded text-right"
                              />
                              <button onClick={() => handleSave(item.field)} className="text-green-600 hover:text-green-800" title="Save">
                                <Save size={16} />
                              </button>
                              <button onClick={() => handleCancel(item.field)} className="text-red-600 hover:text-red-800" title="Cancel">
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="text-right w-20">£{parseFloat(tempValues[item.field] ?? 0).toFixed(2)}</span>
                              <button onClick={() => handleEditToggle(item.field)} className="text-blue-600 hover:text-blue-800" title="Edit">
                                <Edit2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={`£${item.value ? parseFloat(item.value).toFixed(2) : '0.00'}`}
                          readOnly
                          className="text-right w-20 border-none outline-none"
                        />
                      )}
                    </div>
                  ))}

                  <div className="flex justify-between px-3 py-2 border-t border-gray-200">
                    <span>Current Digital Services Cost</span>
                    <input
                      type="text"
                      value={`£${webHostingCost ? parseFloat(webHostingCost).toFixed(2) : '0.00'}`}
                      readOnly
                      className="text-right w-20 border-none outline-none"
                    />
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-800">Current Total Package Cost:</span>
                    <span className="text-xl font-bold text-blue-600">
                      £{(currentTotalPackageCost + parseFloat(webHostingCost ?? 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-6 items-start text-sm mt-6">
              {/* New Monthly Charges */}
              <div>
                <h2 className="text-lg font-bold mb-2">New Monthly Charges (Excl VAT)</h2>
                <div className="border border-gray-300">
                  {newMonthlyChargesItems.map((item, index) => {
                  if (item.isAiReceptionist) return renderAiReceptionistRow(item, index);
                  return (
                  
                    <div 
                      key={index} 
                      className={`flex justify-between px-3 py-2 ${index < newMonthlyChargesItems.length - 1 ? 'border-b border-gray-200' : ''} ${item.editable ? 'bg-blue-50' : ''} ${item.isCalculated ? 'bg-green-50' : ''}`}
                    >
                      <span className="flex items-center">
                        {item.label}
                        {item.isCalculated && (
                          <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            Auto-calculated
                          </span>
                        )}
                      </span>
                      
                      {item.editable ? (
                        <div className="flex items-center space-x-2">
                          {editing[item.field] ? (
                            <>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={tempValues[item.field]}
                                onChange={(e) => handleInputChange(item.field, e.target.value)}
                                className="w-24 px-2 py-1 border border-blue-300 rounded text-right"
                              />
                              <button 
                                onClick={() => handleSave(item.field)}
                                className="text-green-600 hover:text-green-800"
                                title="Save"
                              >
                                <Save size={16} />
                              </button>
                              <button 
                                onClick={() => handleCancel(item.field)}
                                className="text-red-600 hover:text-red-800"
                                title="Cancel"
                              >
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="text-right w-20">£{parseFloat(item.value ?? 0).toFixed(2)}</span>
                              <button 
                                onClick={() => handleEditToggle(item.field)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={`£${item.value ? parseFloat(item.value).toFixed(2) : '0.00'}`}
                          readOnly
                          className={`text-right w-20 border-none outline-none ${item.isCalculated ? 'font-bold text-green-700' : ''}`}
                        />
                      )}
                    </div>
                     
                  );
                })}
                  
                  
                </div>
                <div className="mt-6 pt-4 border-t border-gray-200">
  <div className="flex justify-between items-center">
    <span className="text-lg font-bold text-gray-800">
      New Total Package Cost{aiReceptionistEnabled ? '*' : ''}:
    </span>
    <span className="text-xl font-bold text-blue-600">
      £{typeof newTotalPackageCost === 'number' ? newTotalPackageCost.toFixed(2) : '0.00'}
    </span>
  </div>
  {aiReceptionistEnabled && (
    <p className="text-xs text-amber-800 mt-2 text-right">
      * Includes AI Receptionist at £10.00/month.AI Receptionist inbound call usage at 15p per minute.
    </p>
  )}
</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rental Profile Modal */}
      {showRentalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {isEditingRental ? 'Edit Rental Profile' : 'Rental Profile'}
                </h3>
                <button 
                  onClick={handleCloseRentalModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <RentalProfileForm 
                selectedConnectionId={connectionId}
                onSaveSuccess={handleRentalSaveSuccess}
                onCancel={handleCloseRentalModal}
                initialData={isEditingRental ? rentalData : null}
                isEditing={isEditingRental}
              />
            </div>
          </div>
        </div>
      )}
      
      
    </div>
    
  );

};

export default CostComparisonComponent;